import React, { useState, useEffect } from 'react';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  PencilIcon,
  ArrowTopRightOnSquareIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import { supabase } from '../../services/supabaseClient';

interface OrgBasicData {
  orgId: string;
  orgName: string;
  instanceUrl: string;
  needsReconnect: boolean;
  flowCount: number;
  environmentId: string | null;
  isSandbox: boolean;
  firstConnectedAt: string | null;
  lastSyncedAt: string | null;
}

interface Environment {
  environmentId: string;
  isAddon: boolean;
  addonPrice?: number;
  orgs: OrgBasicData[];
}

interface Discount {
  type: 'percent' | 'fixed';
  percent?: number;
  amount?: number;
}

interface Subscription {
  id: string | null;
  planType: string;
  planName?: string;
  status: string;
  stripeSubscriptionId: string | null;
  currentPeriodEnd: string | null;
  trialEndsAt: string | null;
  monthlyPrice: number;
  billingInterval?: string;
  subtotal?: number;
  discount?: Discount | null;
  lineItems?: any[];
  environments: Environment[];
}

interface ActivityMetrics {
  lastLoginAt: string | null;
  activeDays30: number;
}

interface Account {
  accountId: string;
  accountName: string;
  userEmail: string;
  accountCreatedAt: string | null;
  activity: ActivityMetrics;
  subscription: Subscription | null;
}

export default function AccountsAndSubscriptions() {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedAccounts, setExpandedAccounts] = useState<Set<string>>(new Set());
  const [expandedEnvironments, setExpandedEnvironments] = useState<Set<string>>(new Set());
  const [editingAccount, setEditingAccount] = useState<string | null>(null);
  const [editFormData, setEditFormData] = useState<any>(null);
  const [availableCoupons, setAvailableCoupons] = useState<any[]>([]);
  const [allOrgs, setAllOrgs] = useState<any[]>([]);
  const [selectedPromoCodeType, setSelectedPromoCodeType] = useState<'coupon' | 'promo'>('coupon');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        throw new Error('No active session');
      }

      const response = await fetch('/api/admin/active-accounts-analytics', {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch accounts');
      }

      const data = await response.json();

      // Transform data to match our new structure
      const transformedAccounts = data.connectedOrgs.map((account: any) => {
        // Always create environments array, regardless of subscription status
        const environments = account.environments.map((env: any, index: number) => ({
          environmentId: env.environmentId,
          isAddon: index > 0, // First environment is base, rest are add-ons
          addonPrice: index > 0 ? 99 : undefined, // Environment add-on: $99/mo (from Stripe data if available)
          orgs: env.orgs
        }));

        // Use pricing from Stripe API (already calculated with discounts)
        const monthlyPrice = account.subscription.monthlyPrice || 0;
        const subtotal = account.subscription.subtotal || monthlyPrice;
        const discount = account.subscription.discount || null;
        const lineItems = account.subscription.lineItems || [];

        return {
          accountId: account.accountId,
          accountName: account.accountName,
          userEmail: account.userEmail,
          accountCreatedAt: account.accountCreatedAt || null,
          activity: {
            lastLoginAt: account.activity?.lastLoginAt || null,
            activeDays30: account.activity?.activeDays30 || 0
          },
          subscription: account.subscription.planType ? {
            id: account.subscription.stripeSubscriptionId,
            planType: account.subscription.planType,
            planName: account.subscription.planName || account.subscription.planType,
            status: account.subscriptionStatus,
            stripeSubscriptionId: account.subscription.stripeSubscriptionId,
            currentPeriodEnd: account.subscription.currentPeriodEnd,
            trialEndsAt: account.subscription.trialEndsAt,
            monthlyPrice: monthlyPrice,
            billingInterval: account.subscription.billingInterval || 'month',
            subtotal: subtotal,
            discount: discount,
            lineItems: lineItems,
            environments: environments
          } : {
            // For accounts without subscriptions, create a minimal subscription object
            id: null,
            planType: 'none',
            planName: 'No Plan',
            status: account.subscriptionStatus || 'inactive',
            stripeSubscriptionId: null,
            currentPeriodEnd: null,
            trialEndsAt: null,
            monthlyPrice: 0,
            billingInterval: 'month',
            subtotal: 0,
            discount: null,
            lineItems: [],
            environments: environments
          }
        };
      });

      setAccounts(transformedAccounts);
    } catch (error) {
      console.error('Error fetching accounts:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleAccountExpansion = (accountId: string) => {
    const newExpanded = new Set(expandedAccounts);
    if (newExpanded.has(accountId)) {
      newExpanded.delete(accountId);
    } else {
      newExpanded.add(accountId);
    }
    setExpandedAccounts(newExpanded);
  };

  const toggleEnvironmentExpansion = (envKey: string) => {
    const newExpanded = new Set(expandedEnvironments);
    if (newExpanded.has(envKey)) {
      newExpanded.delete(envKey);
    } else {
      newExpanded.add(envKey);
    }
    setExpandedEnvironments(newExpanded);
  };

  const openEditModal = async (account: Account) => {
    setEditingAccount(account.accountId);

    // Fetch available coupons
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        const response = await fetch('/api/admin/stripe-coupons', {
          headers: { 'Authorization': `Bearer ${session.access_token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setAvailableCoupons(data.coupons || []);
        }
      }
    } catch (error) {
      console.error('Failed to fetch coupons:', error);
    }

    // Flatten all orgs for environment assignment
    const orgs: any[] = [];
    account.subscription.environments.forEach(env => {
      env.orgs.forEach(org => {
        orgs.push({
          ...org,
          currentEnvironmentId: env.environmentId
        });
      });
    });
    setAllOrgs(orgs);

    // Build environment assignments (which orgs belong to which environment)
    const environmentAssignments: Record<string, string[]> = {};
    account.subscription.environments.forEach((env, index) => {
      const envKey = `env-${index}`;
      environmentAssignments[envKey] = env.orgs.map(org => org.orgId);
    });

    setEditFormData({
      accountId: account.accountId,
      accountName: account.accountName,
      planType: account.subscription?.planType === 'none' ? 'standard' : (account.subscription?.planType || 'standard'),
      billingInterval: 'monthly',
      customMonthlyPrice: null,
      customAnnualPrice: null,
      selectedCoupons: [],
      selectedPromoCodes: [],
      environmentAssignments: environmentAssignments,
      stripeSubscriptionId: account.subscription?.stripeSubscriptionId
    });
  };

  const closeEditModal = () => {
    setEditingAccount(null);
    setEditFormData(null);
  };

  const saveSubscriptionChanges = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        alert('Session expired. Please refresh the page.');
        return;
      }

      // Validate enterprise plan has annual pricing
      if (editFormData.planType === 'enterprise' && !editFormData.customAnnualPrice) {
        alert('Enterprise plans require annual pricing to be specified.');
        return;
      }

      const response = await fetch('/api/admin/update-account-subscription', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          accountId: editFormData.accountId,
          planType: editFormData.planType,
          billingInterval: editFormData.billingInterval,
          customMonthlyPrice: editFormData.customMonthlyPrice,
          customAnnualPrice: editFormData.customAnnualPrice,
          selectedCoupons: editFormData.selectedCoupons,
          selectedPromoCodes: editFormData.selectedPromoCodes,
          environmentAssignments: editFormData.environmentAssignments
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update subscription');
      }

      alert(`Subscription updated successfully!\n${data.message || ''}`);
      closeEditModal();
      await fetchAccounts();

    } catch (error: any) {
      console.error('Error saving subscription changes:', error);
      alert(`Failed to update subscription: ${error.message}`);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusColors: Record<string, string> = {
      active: 'bg-green-100 text-green-800',
      trialing: 'bg-blue-100 text-blue-800',
      trial_expired: 'bg-red-100 text-red-800',
      past_due: 'bg-yellow-100 text-yellow-800',
      canceled: 'bg-red-100 text-red-800',
      inactive: 'bg-gray-100 text-gray-800',
    };

    const displayName = status === 'trial_expired' ? 'trial expired' : status;

    return (
      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${statusColors[status] || 'bg-gray-100 text-gray-800'}`}>
        {displayName}
      </span>
    );
  };

  // Helper function for relative time display
  const formatRelativeTime = (dateString: string | null): string => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor(diffMs / (1000 * 60));

    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
    return date.toLocaleDateString();
  };

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
      </div>
    );
  }

  // Filter and sort accounts
  const filteredAccounts = accounts
    .filter(account => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (
        account.accountName.toLowerCase().includes(query) ||
        account.userEmail.toLowerCase().includes(query)
      );
    })
    .sort((a, b) => a.accountName.localeCompare(b.accountName));

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Accounts & Subscriptions</h2>
          <p className="text-sm text-gray-600 mt-1">Manage customer accounts, subscriptions, and connected orgs</p>
        </div>
        <div className="w-64">
          <input
            type="text"
            placeholder="Search accounts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      <div className="space-y-4">
        {filteredAccounts.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
            {searchQuery ? 'No accounts match your search' : 'No accounts found'}
          </div>
        ) : (
          filteredAccounts.map((account) => {
            const isAccountExpanded = expandedAccounts.has(account.accountId);

            return (
              <div key={account.accountId} className="bg-white rounded-lg shadow">
                {/* Account Header */}
                <div
                  className="p-4 cursor-pointer hover:bg-gray-50 flex items-center justify-between"
                  onClick={() => toggleAccountExpansion(account.accountId)}
                >
                  <div className="flex items-center gap-3">
                    {isAccountExpanded ? (
                      <ChevronDownIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
                    ) : (
                      <ChevronRightIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
                    )}
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-semibold text-gray-900">{account.accountName}</h3>
                        {account.accountCreatedAt && (
                          <span className="text-xs text-gray-400">({formatDate(account.accountCreatedAt)})</span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500">{account.userEmail}</p>
                      <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                        <span>Last login: {formatRelativeTime(account.activity.lastLoginAt)}</span>
                        <span>•</span>
                        <span>Active: {account.activity.activeDays30}/30 days</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right text-sm">
                      {account.subscription.planType !== 'none' ? (
                        <>
                          <div className="font-medium text-gray-700">
                            {account.subscription.planName || account.subscription.planType}
                          </div>
                          <div className="text-gray-500">
                            ${account.subscription.monthlyPrice}/{account.subscription.billingInterval === 'year' ? 'yr' : 'mo'}
                            {account.subscription.discount && (
                              <span className="ml-1 text-green-600">
                                ({account.subscription.discount.type === 'percent'
                                  ? `${account.subscription.discount.percent}% off`
                                  : `$${account.subscription.discount.amount} off`})
                              </span>
                            )}
                          </div>
                          {account.subscription.discount && account.subscription.subtotal !== account.subscription.monthlyPrice && (
                            <div className="text-xs text-gray-400 line-through">
                              ${account.subscription.subtotal}/{account.subscription.billingInterval === 'year' ? 'yr' : 'mo'}
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="font-medium text-gray-500">No Subscription</div>
                      )}
                    </div>
                    {getStatusBadge(account.subscription.status)}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openEditModal(account);
                      }}
                      className="p-2 hover:bg-gray-100 rounded-full"
                      title="Edit Subscription"
                    >
                      <PencilIcon className="h-4 w-4 text-gray-600" />
                    </button>
                  </div>
                </div>

                {/* Account Details - Subscription & Environments */}
                {isAccountExpanded && (
                  <div className="border-t border-gray-200">
                    {account.subscription.planType !== 'none' && (
                      <div className="px-6 py-4 bg-gray-50">
                        <div className="text-sm">
                          <span className="font-medium text-gray-700">Subscription Details:</span>
                          {account.subscription.currentPeriodEnd && (
                            <span className="ml-3 text-gray-600">
                              Next billing: {new Date(account.subscription.currentPeriodEnd).toLocaleDateString()}
                            </span>
                          )}
                          {account.subscription.trialEndsAt && (
                            <span className="ml-3 text-gray-600">
                              Trial ends: {new Date(account.subscription.trialEndsAt).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Environments */}
                    {account.subscription.environments.map((environment, envIndex) => {
                      const envKey = `${account.accountId}-${environment.environmentId}`;
                      const isEnvExpanded = expandedEnvironments.has(envKey);
                      const envName = environment.environmentId === 'no-environment'
                        ? 'Standalone Orgs'
                        : environment.environmentId; // Use the production org name as environment name

                      return (
                        <div key={envKey} className="border-t border-gray-100">
                          {/* Environment Header */}
                          <div
                            className="px-6 py-3 bg-white cursor-pointer hover:bg-gray-50 flex items-center justify-between"
                            onClick={() => toggleEnvironmentExpansion(envKey)}
                          >
                            <div className="flex items-center gap-2">
                              {isEnvExpanded ? (
                                <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                              ) : (
                                <ChevronRightIcon className="h-4 w-4 text-gray-400" />
                              )}
                              <span className="font-medium text-gray-700">{envName}</span>
                              <span className="text-sm text-gray-500">
                                ({environment.orgs.length} org{environment.orgs.length !== 1 ? 's' : ''})
                              </span>
                              {environment.isAddon && (
                                <span className="ml-2 px-2 py-0.5 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                                  Additional Environment
                                </span>
                              )}
                              {!environment.isAddon && (
                                <span className="ml-2 px-2 py-0.5 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                                  Base subscription
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Orgs List */}
                          {isEnvExpanded && (
                            <div className="px-6 py-2 bg-gray-50">
                              <div className="space-y-2">
                                {environment.orgs.map((org) => (
                                  <div
                                    key={org.orgId}
                                    className="bg-white rounded-lg border border-gray-200 p-3 hover:border-blue-300 transition-colors"
                                  >
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-3 flex-1 min-w-0">
                                        {/* Org Name & Type */}
                                        <div className="min-w-0 flex-1">
                                          <div className="flex items-center gap-2">
                                            <span className="font-medium text-gray-900 truncate">{org.orgName}</span>
                                            <span className={`px-2 py-0.5 text-xs rounded-full flex-shrink-0 ${
                                              org.isSandbox ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                                            }`}>
                                              {org.isSandbox ? 'Sandbox' : 'Production'}
                                            </span>
                                          </div>
                                          <div className="text-xs text-gray-500 truncate">{org.instanceUrl}</div>
                                          <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                                            <span>Connected: {formatDate(org.firstConnectedAt)}</span>
                                            <span>•</span>
                                            <span>Last sync: {formatRelativeTime(org.lastSyncedAt)}</span>
                                          </div>
                                        </div>

                                        {/* Basic Metrics - Inline */}
                                        <div className="flex items-center gap-4 text-sm flex-shrink-0">
                                          {/* Connection Status */}
                                          {org.needsReconnect ? (
                                            <span className="flex items-center gap-1 text-red-600">
                                              <XCircleIcon className="h-4 w-4" />
                                              <span className="text-xs">Reconnect</span>
                                            </span>
                                          ) : (
                                            <span className="flex items-center gap-1 text-green-600">
                                              <CheckCircleIcon className="h-4 w-4" />
                                              <span className="text-xs">Connected</span>
                                            </span>
                                          )}

                                          {/* Flows */}
                                          <div className="text-center">
                                            <div className="text-sm font-medium text-gray-900">{org.flowCount}</div>
                                            <div className="text-xs text-gray-500">Flows</div>
                                          </div>
                                        </div>

                                        {/* Preview Dashboard Link */}
                                        <a
                                          href={`/${org.orgId}/dashboard`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="flex items-center gap-1 px-3 py-1.5 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors flex-shrink-0"
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          Preview Dashboard
                                          <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                                        </a>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Edit Subscription Modal */}
      {editingAccount && editFormData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-4xl w-full my-8 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-semibold text-gray-900 mb-6">Manage Subscription</h3>

            <div className="space-y-6">
              {/* Account Info */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Account</label>
                <input
                  type="text"
                  value={editFormData.accountName}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600"
                />
              </div>

              {/* Plan Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Plan Type</label>
                <select
                  value={editFormData.planType}
                  onChange={(e) => setEditFormData({ ...editFormData, planType: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="standard">Standard Plan - $99/mo</option>
                  <option value="professional">Professional Plan - $299/mo (includes 1 environment)</option>
                  <option value="enterprise">Enterprise Plan (custom pricing)</option>
                </select>
              </div>

              {/* Billing Interval */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Billing Interval</label>
                <div className="flex gap-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      checked={editFormData.billingInterval === 'monthly'}
                      onChange={() => setEditFormData({ ...editFormData, billingInterval: 'monthly' })}
                      className="mr-2"
                    />
                    <span className="text-sm">Monthly</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      checked={editFormData.billingInterval === 'annual'}
                      onChange={() => setEditFormData({ ...editFormData, billingInterval: 'annual' })}
                      className="mr-2"
                    />
                    <span className="text-sm">Annual</span>
                  </label>
                </div>
                {editFormData.planType === 'enterprise' && editFormData.billingInterval === 'monthly' && (
                  <p className="text-sm text-yellow-600 mt-1">⚠️ Enterprise plans require annual billing</p>
                )}
              </div>

              {/* Custom Pricing */}
              {editFormData.planType === 'enterprise' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Annual Price (required for Enterprise)
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-2 text-gray-500">$</span>
                      <input
                        type="number"
                        value={editFormData.customAnnualPrice === null ? '' : editFormData.customAnnualPrice}
                        onChange={(e) => {
                          const val = e.target.value;
                          setEditFormData({
                            ...editFormData,
                            customAnnualPrice: val === '' ? null : parseFloat(val)
                          });
                        }}
                        className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-md"
                        placeholder="Enter annual price"
                        step="100"
                        min="0"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Override Pricing (for comps/discounts) */}
              {(editFormData.planType === 'standard' || editFormData.planType === 'professional') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Override Pricing (Optional)
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Monthly Price Override</label>
                      <div className="relative">
                        <span className="absolute left-3 top-2 text-gray-500">$</span>
                        <input
                          type="number"
                          value={editFormData.customMonthlyPrice === null ? '' : editFormData.customMonthlyPrice}
                          onChange={(e) => {
                            const val = e.target.value;
                            setEditFormData({
                              ...editFormData,
                              customMonthlyPrice: val === '' ? null : parseFloat(val)
                            });
                          }}
                          className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-md"
                          placeholder={editFormData.planType === 'professional' ? '299' : '99'}
                          step="0.01"
                          min="0"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Annual Price Override</label>
                      <div className="relative">
                        <span className="absolute left-3 top-2 text-gray-500">$</span>
                        <input
                          type="number"
                          value={editFormData.customAnnualPrice === null ? '' : editFormData.customAnnualPrice}
                          onChange={(e) => {
                            const val = e.target.value;
                            setEditFormData({
                              ...editFormData,
                              customAnnualPrice: val === '' ? null : parseFloat(val)
                            });
                          }}
                          className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-md"
                          placeholder={editFormData.planType === 'professional' ? '2990' : '990'}
                          step="0.01"
                          min="0"
                        />
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Leave blank to use standard pricing. Set to $0 for complimentary access.</p>
                </div>
              )}

              {/* Coupons and Promo Codes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Apply Discounts (Optional)</label>

                {/* Toggle between coupon ID and promo code */}
                <div className="flex gap-4 mb-3">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      checked={selectedPromoCodeType === 'coupon'}
                      onChange={() => setSelectedPromoCodeType('coupon')}
                      className="mr-2"
                    />
                    <span className="text-sm">Apply Coupon ID</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      checked={selectedPromoCodeType === 'promo'}
                      onChange={() => setSelectedPromoCodeType('promo')}
                      className="mr-2"
                    />
                    <span className="text-sm">Apply Promo Code</span>
                  </label>
                </div>

                {selectedPromoCodeType === 'coupon' ? (
                  <>
                    <select
                      value={editFormData.selectedCoupons[0] || ''}
                      onChange={(e) => {
                        setEditFormData({
                          ...editFormData,
                          selectedCoupons: e.target.value ? [e.target.value] : [],
                          selectedPromoCodes: []
                        });
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                      <option value="">No coupon</option>
                      {availableCoupons.map(coupon => (
                        <option key={coupon.id} value={coupon.id}>
                          {coupon.name} - {coupon.percent_off ? `${coupon.percent_off}% off` : `$${coupon.amount_off / 100} off`}
                          {coupon.duration === 'forever' ? ' (forever)' : coupon.duration === 'once' ? ' (one-time)' : ` (${coupon.duration_in_months} months)`}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">Stripe only allows one coupon per subscription</p>
                  </>
                ) : (
                  <>
                    <select
                      value={editFormData.selectedPromoCodes[0] || ''}
                      onChange={(e) => {
                        setEditFormData({
                          ...editFormData,
                          selectedPromoCodes: e.target.value ? [e.target.value] : [],
                          selectedCoupons: []
                        });
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                      <option value="">No promo code</option>
                      {availableCoupons.flatMap(coupon =>
                        (coupon.promotion_codes || []).map((promo: any) => (
                          <option key={promo.id} value={promo.code}>
                            {promo.code} ({coupon.name}) - {coupon.percent_off ? `${coupon.percent_off}% off` : `$${coupon.amount_off / 100} off`}
                            {!promo.active && ' [INACTIVE]'}
                          </option>
                        ))
                      )}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">Promo codes are customer-friendly redemption codes (e.g., "CertifyCRM")</p>
                  </>
                )}
              </div>

              {/* Environment Management (Pro/Enterprise only) */}
              {(editFormData.planType === 'professional' || editFormData.planType === 'enterprise') && allOrgs.length > 0 && (
                <div className="border-t pt-6">
                  <h4 className="text-md font-semibold text-gray-900 mb-3">Environment Management</h4>
                  <p className="text-sm text-gray-600 mb-4">
                    Assign production orgs to environments. Each environment includes 1 production org + up to 5 sandboxes.
                    <br />
                    <span className="font-medium">Pricing:</span> First environment included, additional environments +$99/mo or +$990/yr
                  </p>

                  {/* List of production orgs that can be assigned to environments */}
                  <div className="space-y-3">
                    {allOrgs.filter(org => !org.isSandbox).map((org, index) => (
                      <div key={org.orgId} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium text-gray-900">{org.orgName}</div>
                            <div className="text-xs text-gray-500">{org.instanceUrl}</div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-600">Environment:</span>
                            <select
                              value={Object.keys(editFormData.environmentAssignments).find(key =>
                                editFormData.environmentAssignments[key].includes(org.orgId)
                              ) || 'new'}
                              onChange={(e) => {
                                const newAssignments = { ...editFormData.environmentAssignments };
                                // Remove org from old environment
                                Object.keys(newAssignments).forEach(key => {
                                  newAssignments[key] = newAssignments[key].filter(id => id !== org.orgId);
                                });
                                // Add to new environment
                                if (e.target.value === 'new') {
                                  const newKey = `env-${Object.keys(newAssignments).length}`;
                                  newAssignments[newKey] = [org.orgId];
                                } else {
                                  if (!newAssignments[e.target.value]) {
                                    newAssignments[e.target.value] = [];
                                  }
                                  newAssignments[e.target.value].push(org.orgId);
                                }
                                setEditFormData({ ...editFormData, environmentAssignments: newAssignments });
                              }}
                              className="px-2 py-1 border border-gray-300 rounded text-sm"
                            >
                              <option value="new">New Environment {index > 0 && `(+$99/mo)`}</option>
                              {Object.keys(editFormData.environmentAssignments).map((envKey, envIndex) => (
                                <option key={envKey} value={envKey}>
                                  Environment {envIndex + 1} {envIndex > 0 && `(+$99/mo)`}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Calculate total environments */}
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                    <div className="text-sm">
                      <span className="font-medium text-gray-900">Total Environments:</span>
                      <span className="ml-2 text-gray-700">
                        {Object.keys(editFormData.environmentAssignments).filter(key =>
                          editFormData.environmentAssignments[key].length > 0
                        ).length}
                      </span>
                      {Object.keys(editFormData.environmentAssignments).filter(key =>
                        editFormData.environmentAssignments[key].length > 0
                      ).length > 1 && (
                        <span className="ml-3 text-blue-700 font-medium">
                          (+{(Object.keys(editFormData.environmentAssignments).filter(key =>
                            editFormData.environmentAssignments[key].length > 0
                          ).length - 1) * 99}/mo for additional environments)
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="mt-8 flex gap-3 justify-end border-t pt-6">
              <button
                onClick={closeEditModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={saveSubscriptionChanges}
                disabled={editFormData.planType === 'enterprise' && !editFormData.customAnnualPrice}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                Save Subscription
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
